import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { fileUrl, type AttachmentKind } from '../api/client.ts';
import type { MessageView } from '../chat/ChatContext.tsx';
import { savesViaShell, saveViaShell } from '../lib/download.ts';
import {
  clampPan,
  distance,
  FIT,
  isDoubleTap,
  midpoint,
  toggleZoom,
  zoomTo,
  type Box,
  type Point,
  type Tap,
  type Zoom,
} from '../lib/zoom.ts';
import './ImageViewer.css';

/** One thing the viewer can show; an album is a list of these, a photo is one. */
export interface ViewerItem {
  /** Which attachment of the message, or `null` for a message that is one file. */
  index: number | null;
  kind: AttachmentKind;
  name: string | null;
  /**
   * The message these bytes are under, when it is not the one in `ViewerImage.id`.
   *
   * An album's slides all share one message, so they leave this off. A shelf of
   * media in the profile does not: consecutive slides there are usually different
   * messages, and each has to say which.
   */
  messageId?: string;
}

/** Everything the viewer needs; kept serialisable because it rides in history state. */
export interface ViewerImage {
  /** The message the bytes belong to — attachments are addressed by it. */
  id: string;
  name: string | null;
  /**
   * An album's items and which of them was tapped. Absent on a lone image, which
   * is a group of one with nothing to page to.
   *
   * `messageId` on an item is what the profile's grid adds: an album's slides all
   * belong to one message and take `id` above, but a shelf of media is drawn from
   * the whole conversation, so each slide carries the message its bytes are under.
   *
   * `total` is the length of that shelf rather than of `items`, which is only what
   * has been paged in so far — «58 из 60» has to know the 60 before anyone has
   * scrolled to it. Absent on an album, where the two are the same number.
   */
  group?: {
    items: ViewerItem[];
    at: number;
    caption: string | null;
    total?: number;
  };
}

/** What tapping tile `index` of an album opens. */
export function viewerForAlbum(message: MessageView, index: number): ViewerImage {
  const items: ViewerItem[] = message.attachments.map((item) => ({
    index: item.index,
    kind: item.kind,
    name: item.fileName,
  }));
  return {
    id: message.id,
    name: items[index]?.name ?? null,
    group: {
      items,
      at: index,
      // One caption for the group, so it is shown once here rather than being
      // repeated under every slide as if each picture had its own.
      caption: message.body?.trim() || null,
    },
  };
}

/**
 * What tapping tile `at` of a conversation's media shelf opens.
 *
 * Every slide names its own message, because unlike an album these came from all
 * over the conversation. `total` is the shelf's length rather than the loaded
 * list's, so the counter can say «58 из 60» before the 60th has been fetched.
 */
export function viewerForConversationMedia(
  items: { messageId: string; index: number | null; kind: AttachmentKind; fileName: string }[],
  at: number,
  total: number,
): ViewerImage {
  const slides: ViewerItem[] = items.map((item) => ({
    index: item.index,
    kind: item.kind,
    name: item.fileName,
    messageId: item.messageId,
  }));
  return {
    // The tapped slide's message, so that a viewer opened on it addresses the
    // right bytes even before the group below is consulted.
    id: items[at]?.messageId ?? '',
    name: slides[at]?.name ?? null,
    // No caption: these photographs were sent at different times under different
    // words, and there is no one line that belongs to all of them.
    group: { items: slides, at, caption: null, total },
  };
}

/**
 * Which message a slide's bytes are under. An album's slides inherit the one in
 * `ViewerImage.id`; a media shelf's each carry their own.
 */
function slideMessageId(image: ViewerImage, item: ViewerItem): string {
  return item.messageId ?? image.id;
}

/** A finger has to travel this far before it stops counting as a tap. */
const TAP_SLOP = 8;

/** …and this far sideways before letting go moves to the next picture. */
const PAGE_SLOP = 50;

/** …and this far down (or up) before letting go closes the viewer. */
const PULL_SLOP = 90;

/**
 * How much travel takes the backdrop from opaque to as clear as it gets. The
 * floor keeps the thread from reading as fully revealed while the finger is
 * still down — the picture is leaving, not gone.
 */
const PULL_FADE_RANGE = 400;
const PULL_FADE_FLOOR = 0.2;

/** What one gesture — from the first finger down to the last one up — remembers. */
interface Gesture {
  /** Where the first finger landed, which is what «did it move» is measured from. */
  origin: Point;
  /** Whether the gesture started on the picture rather than on the backdrop. */
  onPicture: boolean;
  moved: boolean;
  pinched: boolean;
  /** The spread and the scale the pinch began at; every frame is measured against them. */
  spread: number;
  scale: number;
  /** The midpoint of the last frame, so two fingers can drag as well as pinch. */
  anchor: Point | null;
}

const NO_GESTURE: Gesture = {
  origin: { x: 0, y: 0 },
  onPicture: false,
  moved: false,
  pinched: false,
  spread: 0,
  scale: 1,
  anchor: null,
};

/**
 * Fullscreen attachment viewer.
 *
 * It deliberately renders in-app instead of following a link to the file: an
 * installed PWA opening `target="_blank"` lands in a fresh top-level context
 * whose history is empty, so the hardware back button closes the app rather
 * than returning to the thread. The caller owns the history entry that back
 * pops (see ChatScreen); this component only reports that it wants to close.
 *
 * The zoom is ours rather than the browser's. `maximum-scale=1` in the viewport
 * meta stops Android from pinching the page, and on iOS pinching the page would
 * scale the layout around a `position: fixed` overlay instead of the picture —
 * so neither platform has a native gesture worth borrowing here. Pointer events
 * over a `touch-action: none` surface behave the same on both.
 *
 * An album pages sideways as well, and the two gestures share one finger without
 * fighting because they are never both available: below fit scale there is
 * nothing hidden to pan into view, which is exactly when a sideways drag can
 * only have meant «the next one». Zoom in and the same drag pans again.
 *
 * The same reasoning gives the vertical drag away: at fit scale there is
 * nothing above or below the picture either, so pulling it off the screen can
 * only have meant «закрыть» — the gesture every iOS photo viewer has taught.
 * Whichever axis leaves the tap slop first owns the whole drag, so a page turn
 * that wanders downward never starts closing halfway through.
 */
export function ImageViewer({ image, onClose }: { image: ViewerImage; onClose: () => void }) {
  const slides: ViewerItem[] = image.group?.items ?? [
    { index: null, kind: 'photo', name: image.name },
  ];
  const [at, setAt] = useState(() => Math.min(Math.max(image.group?.at ?? 0, 0), slides.length - 1));
  const [zoom, setZoom] = useState<Zoom>(FIT);
  /** How far the track has followed the finger, and whether it still is. */
  const [dragX, setDragX] = useState(0);
  const [paging, setPaging] = useState(false);
  /** The vertical twin: how far the pull towards closing has come. */
  const [dragY, setDragY] = useState(0);
  const [pulling, setPulling] = useState(false);

  const scrim = useRef<HTMLDivElement | null>(null);
  const picture = useRef<HTMLImageElement | null>(null);
  /** Every finger currently down, by pointer id — `size` is what says pinch or pan. */
  const live = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture>(NO_GESTURE);
  const lastTap = useRef<Tap | null>(null);
  /** Read by the pointer handlers, which run between renders. */
  const index = useRef(at);
  index.current = at;
  /** Which way the one-finger drag committed, for as long as it is down. */
  const axis = useRef<'page' | 'pull' | null>(null);
  /** The video of every slide that has one, by position, so paging can stop them. */
  const videos = useRef(new Map<number, HTMLVideoElement>());

  const slide = slides[at]!;
  const caption = image.group?.caption ?? null;

  // The handlers run between renders and need the transform they are amending,
  // not the one React last painted.
  const current = useRef<Zoom>(FIT);
  const apply = useCallback((next: Zoom) => {
    current.current = next;
    setZoom(next);
  }, []);

  const step = useCallback(
    (delta: number) => {
      setAt((was) => Math.min(Math.max(was + delta, 0), slides.length - 1));
    },
    [slides.length],
  );

  /**
   * `offsetWidth`/`offsetHeight` are the size the img *lays out* at, which is
   * unaffected by whatever transform is on it — so this stays the fit size no
   * matter how far in the zoom is. The container is the whole viewport rather
   * than the scrim's padded box, so a zoomed photo may cover the padding too.
   */
  const measure = useCallback((): { box: Box; container: Box; centre: Point } => {
    const img = picture.current;
    const container = { width: window.innerWidth, height: window.innerHeight };
    return {
      box: { width: img?.offsetWidth ?? 0, height: img?.offsetHeight ?? 0 },
      container,
      centre: { x: container.width / 2, y: container.height / 2 },
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      // The arrows are what a desktop has instead of a swipe, and the chevrons
      // they mirror are already on screen.
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  /** A different picture is a fresh view of it — including the next of a group. */
  useEffect(() => {
    apply(FIT);
    lastTap.current = null;
  }, [image.id, at, apply]);

  /**
   * Paging away from a video stops it. The neighbouring slide stays mounted so
   * that it is ready to be swiped to, and a video left mounted goes on playing
   * out of sight — through the next one, and the one after that.
   */
  useEffect(() => {
    for (const [position, video] of videos.current) {
      if (position !== at) video.pause();
    }
  }, [at]);

  useEffect(() => {
    const node = scrim.current;
    if (!node) return;

    // React attaches `wheel` at the root passively, so preventing the page from
    // scrolling behind the viewer has to be done with a listener of our own.
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const { box, container, centre } = measure();
      // ctrlKey is how a trackpad reports a pinch; a bare wheel is a mouse, and
      // its notches are coarser, so it gets the gentler divisor.
      const step = Math.exp(-event.deltaY / (event.ctrlKey ? 100 : 300));
      const anchor = { x: event.clientX, y: event.clientY };
      apply(
        clampPan(zoomTo(current.current, current.current.scale * step, anchor, centre), box, container),
      );
    };
    // Safari before `touch-action` support has its own pinch, which would zoom
    // the page out from under us.
    const stopNativePinch = (event: Event) => event.preventDefault();

    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('gesturestart', stopNativePinch);
    node.addEventListener('gesturechange', stopNativePinch);
    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('gesturestart', stopNativePinch);
      node.removeEventListener('gesturechange', stopNativePinch);
    };
  }, [apply, measure]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A video slide plays with its own controls, and capturing the pointer here
    // would take every tap on them. Paging it is the chevrons' job.
    if (event.target instanceof HTMLVideoElement) return;

    const spot = { x: event.clientX, y: event.clientY };
    // Read the target before capturing: from the next event on, capture
    // retargets everything to the scrim and «was this the picture» is lost.
    const onPicture = event.target === picture.current;
    live.current.set(event.pointerId, spot);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Capture is what keeps a finger that wanders off the picture — or off
      // the screen edge — attached to the gesture. Some mobile browsers refuse
      // it anyway, and a refusal must not take the zoom down with it: the
      // pointer is already recorded above, so the gesture still runs.
    }

    if (live.current.size === 1) {
      gesture.current = { ...NO_GESTURE, origin: spot, onPicture };
      return;
    }
    const fingers = [...live.current.values()];
    const [a, b] = [fingers[0]!, fingers[1]!];
    // A second finger is never a tap, whatever the first one was doing — nor a
    // page turn or a pull, so whatever the first one had dragged is put back.
    endDrag();
    gesture.current = {
      ...gesture.current,
      moved: true,
      pinched: true,
      spread: distance(a, b),
      scale: current.current.scale,
      anchor: midpoint(a, b),
    };
  };

  function endDrag(): void {
    axis.current = null;
    setPaging(false);
    setDragX(0);
    setPulling(false);
    setDragY(0);
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const previous = live.current.get(event.pointerId);
    if (!previous) return;
    const spot = { x: event.clientX, y: event.clientY };
    live.current.set(event.pointerId, spot);
    if (distance(gesture.current.origin, spot) > TAP_SLOP) gesture.current.moved = true;

    const { box, container, centre } = measure();
    const fingers = [...live.current.values()];

    if (fingers.length >= 2) {
      const [a, b] = [fingers[0]!, fingers[1]!];
      const { spread, scale, anchor } = gesture.current;
      if (spread <= 0) return;
      const between = midpoint(a, b);
      const zoomed = zoomTo(current.current, (scale * distance(a, b)) / spread, between, centre);
      // Two fingers drag as well as pinch: the scale is anchored between them,
      // and the picture then follows wherever that point itself has moved.
      const dx = anchor ? between.x - anchor.x : 0;
      const dy = anchor ? between.y - anchor.y : 0;
      gesture.current.anchor = between;
      apply(clampPan({ scale: zoomed.scale, x: zoomed.x + dx, y: zoomed.y + dy }, box, container));
      return;
    }

    // At fit scale there is nothing hidden to drag into view, which is what
    // frees the same drag to turn the page — or to pull the picture off the
    // screen. The first axis out of the tap slop owns the drag: a wobble on
    // the way to a double tap must not slide the track a pixel, and a page
    // turn must not start closing because the finger sagged.
    if (current.current.scale <= 1) {
      const dx = spot.x - gesture.current.origin.x;
      const dy = spot.y - gesture.current.origin.y;
      if (axis.current === null) {
        if (Math.abs(dx) <= TAP_SLOP && Math.abs(dy) <= TAP_SLOP) return;
        axis.current = Math.abs(dy) > Math.abs(dx) ? 'pull' : 'page';
      }
      if (axis.current === 'page') {
        // A lone picture has nothing to page to; the drag is claimed anyway so
        // that sagging later does not turn it into a pull halfway through.
        if (slides.length < 2) return;
        setPaging(true);
        setDragX(dx);
      } else {
        setPulling(true);
        setDragY(dy);
      }
      return;
    }
    apply(
      clampPan(
        {
          scale: current.current.scale,
          x: current.current.x + spot.x - previous.x,
          y: current.current.y + spot.y - previous.y,
        },
        box,
        container,
      ),
    );
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const spot = live.current.get(event.pointerId);
    if (!spot) return;
    live.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // The other finger is still down: the pinch is not over yet, and the one
    // that is left should carry on panning from where it is rather than jumping.
    if (live.current.size > 0) {
      gesture.current.origin = [...live.current.values()][0]!;
      gesture.current.anchor = null;
      gesture.current.spread = 0;
      return;
    }

    if (axis.current !== null) {
      const dx = spot.x - gesture.current.origin.x;
      const dy = spot.y - gesture.current.origin.y;
      // Far enough is the answer in that direction — one picture over, or the
      // viewer closed; anything short of it springs back to where it was.
      if (axis.current === 'page') {
        if (dx <= -PAGE_SLOP) step(1);
        else if (dx >= PAGE_SLOP) step(-1);
      } else if (Math.abs(dy) >= PULL_SLOP) {
        onClose();
      }
      endDrag();
      gesture.current = NO_GESTURE;
      lastTap.current = null;
      return;
    }

    const { moved, pinched, onPicture } = gesture.current;
    gesture.current = NO_GESTURE;
    if (moved || pinched) {
      lastTap.current = null;
      return;
    }

    // A tap on the backdrop closes, as it always has. A tap on the picture is
    // half of a possible double tap, so it does nothing on its own — otherwise
    // the viewer would have to sit on every tap for 300ms to find out.
    if (!onPicture) {
      onClose();
      return;
    }

    const tap: Tap = { ...spot, at: Date.now() };
    if (isDoubleTap(lastTap.current, tap)) {
      lastTap.current = null;
      const { box, container, centre } = measure();
      apply(clampPan(toggleZoom(current.current, tap, centre), box, container));
      return;
    }
    lastTap.current = tap;
  };

  const onPointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    live.current.delete(event.pointerId);
    if (live.current.size === 0) {
      gesture.current = NO_GESTURE;
      endDrag();
    }
    lastTap.current = null;
  };

  const stopGesture = (event: React.PointerEvent<HTMLElement>) => event.stopPropagation();

  /**
   * The web leaves the anchor alone: same origin, `download` attribute, the
   * browser's own UI. Inside a shell that anchor silently does nothing (see
   * lib/download.ts), so the tap is taken over and routed through the share
   * sheet instead.
   */
  const onDownload = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!savesViaShell) return;
    event.preventDefault();
    void saveViaShell(
      fileUrl(slideMessageId(image, slide), slide.index === null ? {} : { index: slide.index }),
      slide.name ?? 'файл',
    );
  };

  /** How far along the pull is, 0 at rest — what fades the backdrop out. */
  const pullFade = Math.max(1 - Math.abs(dragY) / PULL_FADE_RANGE, PULL_FADE_FLOOR);

  return (
    <div
      ref={scrim}
      className="viewer-scrim"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      role="dialog"
      aria-modal="true"
      aria-label={slide.name ?? 'изображение'}
    >
      {/* The dark ground is its own layer rather than the scrim's background,
          so the pull towards closing can fade the room without fading the
          picture that is being pulled through it. */}
      <div
        className="viewer-backdrop"
        style={{ opacity: pullFade, transition: pulling ? 'none' : 'opacity 250ms ease' }}
      />

      <div className="viewer-actions" onPointerDown={stopGesture} onPointerUp={stopGesture}>
        {/* The link the bubble used to be: without it there is no way left to
            get at the original bytes. */}
        <a
          className="viewer-action"
          href={fileUrl(slideMessageId(image, slide), {
            download: true,
            ...(slide.index === null ? {} : { index: slide.index }),
          })}
          download={slide.name ?? ''}
          onClick={onDownload}
          aria-label="Скачать"
        >
          <Download size={18} />
        </a>
        <button type="button" className="viewer-action" onClick={onClose} aria-label="Закрыть">
          <X size={18} />
        </button>
      </div>

      {/* «2 / 5», top centre, so paging never leaves anyone wondering how much
          of the group is left. A single picture is not a group and says nothing. */}
      {/* «из» rather than a slash once the group is a shelf of media: an album of
          ten reads fine as 3 / 10, but the profile's counter is a sentence about a
          conversation, and `total` is the whole shelf rather than what has been
          paged in — see ViewerImage.group.total. */}
      {slides.length > 1 && (
        <div className="viewer-counter" aria-live="polite">
          {image.group?.total === undefined
            ? `${at + 1} / ${slides.length}`
            : `${at + 1} из ${image.group.total}`}
        </div>
      )}

      <div
        className="viewer-track"
        style={{
          transform: `translate(calc(${-at * 100}% + ${dragX}px), ${dragY}px)`,
          transition: paging || pulling ? 'none' : 'transform 250ms ease',
        }}
      >
        {/* Keyed by message *and* index: inside an album the index alone is
            unique, but a shelf of media is drawn from the whole conversation,
            where `null` and `0` both recur as often as there are messages. */}
        {slides.map((item, position) => (
          <div
            className="viewer-slide"
            key={`${slideMessageId(image, item)}:${item.index ?? 'single'}`}
          >
            {/* Only what is on screen and what is one step from it: a group of
                ten would otherwise fetch ten full-resolution photographs to
                show one. */}
            {Math.abs(position - at) > 1 ? null : item.kind === 'video' ? (
              <video
                ref={(node) => {
                  if (node) videos.current.set(position, node);
                  else videos.current.delete(position);
                }}
                className="viewer-video"
                src={fileUrl(slideMessageId(image, item), item.index === null ? {} : { index: item.index })}
                controls
                playsInline
                preload={position === at ? 'metadata' : 'none'}
              />
            ) : (
              <img
                ref={position === at ? picture : undefined}
                className={position === at && zoom.scale > 1 ? 'viewer-image is-zoomed' : 'viewer-image'}
                src={fileUrl(slideMessageId(image, item), item.index === null ? {} : { index: item.index })}
                alt={item.name ?? ''}
                draggable={false}
                style={
                  position === at
                    ? { transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})` }
                    : undefined
                }
              />
            )}
          </div>
        ))}
      </div>

      {/* Hidden at the ends rather than disabled: there is nothing there, and a
          greyed-out chevron over a photograph is one more thing on the picture. */}
      {at > 0 && (
        <button
          type="button"
          className="viewer-page viewer-page-prev"
          onPointerDown={stopGesture}
          onPointerUp={stopGesture}
          onClick={() => step(-1)}
          aria-label="Предыдущее"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {at < slides.length - 1 && (
        <button
          type="button"
          className="viewer-page viewer-page-next"
          onPointerDown={stopGesture}
          onPointerUp={stopGesture}
          onClick={() => step(1)}
          aria-label="Следующее"
        >
          <ChevronRight size={18} />
        </button>
      )}

      {/* The group's caption, once — it belongs to the message rather than to
          whichever picture happens to be in view. */}
      {caption !== null && (
        <div className="viewer-caption" onPointerDown={stopGesture} onPointerUp={stopGesture}>
          {caption}
        </div>
      )}
    </div>
  );
}
